/**
 * ShipmentLookupDialog — P0.19 SPRINT (Aug 2026).
 *
 * Medewerker-lookup dialoog waarmee vanuit ConsolidationDetail (of
 * andere modules) een W-nummer/alias/klant kan worden opgezocht ZONDER
 * de huidige module te verlaten.
 *
 * Vindt shipments ongeacht:
 *   • warehouselijst-filter
 *   • consolidation_id
 *   • warehouse_flow_status
 *   • archived/departed
 *
 * Elke hit toont een compacte kaart met acties:
 *   • Open volledig ShipmentDetail (nieuwe tab)
 *   • Preview Warehouse Receipt (inline PDF via DocumentPreviewModal)
 *   • Print Warehouse Receipt (opent in nieuw tabblad met auto-print)
 *   • Print Shipping Label (nieuw tabblad)
 *
 * Data-testids:
 *   shipment-lookup-dialog
 *   shipment-lookup-input
 *   shipment-lookup-search-btn
 *   shipment-lookup-result-{id}
 *   shipment-lookup-open-{id}
 *   shipment-lookup-warehouse-receipt-{id}
 *   shipment-lookup-shipping-label-{id}
 *   shipment-lookup-close
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Search, X, ExternalLink, Package, Printer, FileText,
  Loader2, AlertTriangle,
} from "lucide-react";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

const STATUS_BADGE_COLOR = {
  aangemaakt: "bg-blue-50 text-blue-800 border-blue-200",
  in_warehouse: "bg-amber-50 text-amber-800 border-amber-200",
  received: "bg-emerald-50 text-emerald-800 border-emerald-200",
  prepared: "bg-cyan-50 text-cyan-800 border-cyan-200",
  loaded: "bg-purple-50 text-purple-800 border-purple-200",
  shipped: "bg-indigo-50 text-indigo-800 border-indigo-200",
  delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  hold: "bg-red-50 text-red-800 border-red-200",
};

export default function ShipmentLookupDialog({ onClose, onOpenShipment, initialQuery = "" }) {
  const { hasPermission } = useAuth();
  const { runTask } = useGlobalLoading();
  const [q, setQ] = useState(initialQuery);
  const initialSearch = useRef(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const canPrintWarehouseReceipt = hasPermission?.("warehouse_receipt:print");
  const canPrintShippingLabel = hasPermission?.("shipping_label:print");

  const doSearch = useCallback(async () => {
    if (!q.trim() || q.trim().length < 2) {
      toast.error("Voer minimaal 2 tekens in");
      return;
    }
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const { data } = await adminApi.get(
        `/admin/shipments/lookup?q=${encodeURIComponent(q.trim())}&limit=25`
      );
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err) || "Zoeken mislukt");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    if (initialQuery && !initialSearch.current) {
      initialSearch.current = true;
      doSearch();
    }
  }, [initialQuery, doSearch]);

  const openShipment = (shipment) => {
    if (onOpenShipment) {
      onOpenShipment(shipment);
    } else if (shipment.id) {
      window.open(`/admin?section=warehouse&shipment_id=${encodeURIComponent(shipment.id)}`, "_blank", "noopener");
    }
    onClose();
  };

  const printWarehouseReceipt = async (shipment) => {
    try {
      await runTask({
        id: `lookup-wr-${shipment.id}`,
        message: "Warehouse Receipt wordt geladen…",
        successMessage: "Printvoorbeeld geopend",
        run: async () => {
          const resp = await adminApi.get(
            `/admin/shipping-labels/${shipment.id}/warehouse-receipt/pdf?print_action=true`,
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
          const w = window.open(url, "_blank", "noopener");
          if (w) {
            // Auto-print als de tab volledig geladen is
            w.onload = () => setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 300);
          }
          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Warehouse Receipt laden mislukt");
      }
    }
  };

  const printShippingLabel = async (shipment) => {
    try {
      await runTask({
        id: `lookup-sl-${shipment.id}`,
        message: "Shipping Label wordt geladen…",
        successMessage: "Printvoorbeeld geopend",
        run: async () => {
          const resp = await adminApi.get(
            `/admin/shipping-labels/${shipment.id}/pdf`,
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
          const w = window.open(url, "_blank", "noopener");
          if (w) w.onload = () => setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 300);
          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Shipping Label laden mislukt");
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="shipment-lookup-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#082c50] text-white">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            <div>
              <div className="text-sm font-bold">Zending opzoeken</div>
              <div className="text-[10px] opacity-70">
                Zoek op W-nummer, alias, klant, ontvanger of documentnummer
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded"
            data-testid="shipment-lookup-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Zoekbalk */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
                placeholder="Bijvoorbeeld W5999NL010001 of 'Rayllen'"
                className="pl-8 rounded-none font-mono"
                autoFocus
                data-testid="shipment-lookup-input"
              />
            </div>
            <Button
              onClick={doSearch}
              disabled={loading}
              className="rounded-none bg-[#0055FF] text-white hover:bg-[#0044CC]"
              data-testid="shipment-lookup-search-btn"
            >
              {loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Search className="w-4 h-4" />}
              <span className="ml-1.5">Zoeken</span>
            </Button>
          </div>
        </div>

        {/* Resultaten */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#082c50]" />
              <p className="text-xs text-gray-500 mt-2">Zending wordt opgezocht…</p>
            </div>
          ) : error ? (
            <div className="p-4">
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4" /> {error}
              </div>
            </div>
          ) : !searched ? (
            <div className="p-8 text-center text-xs text-gray-400">
              Voer een W-nummer, klantnaam of ontvangernaam in en klik zoeken.
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-xs text-gray-400">
              Geen zendingen gevonden voor &quot;{q}&quot;. Controleer of het W-nummer klopt.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="p-3 hover:bg-gray-50"
                  data-testid={`shipment-lookup-result-${it.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-mono text-sm font-bold text-[#082c50]">
                          {it.tracking_number || "-"}
                        </span>
                        <span
                          className={`text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 border ${
                            STATUS_BADGE_COLOR[it.status] || "bg-gray-50 text-gray-700 border-gray-200"
                          }`}
                        >
                          {it.status || "-"}
                        </span>
                        {it.on_hold && (
                          <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 bg-red-50 text-red-800 border border-red-200">
                            HOLD
                          </span>
                        )}
                        {it.consolidation_id && (
                          <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 bg-purple-50 text-purple-800 border border-purple-200">
                            In consolidatie
                          </span>
                        )}
                        {it.archived && (
                          <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 bg-gray-100 text-gray-700 border border-gray-300">
                            Gearchiveerd
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-700">
                        {it.customer_name} → {it.recipient_city || it.destination_country_code || "-"}
                      </div>
                      {(it.tracking_aliases?.length || it.legacy_tracking_number) && (
                        <div className="text-[10px] text-gray-500 mt-0.5 font-mono">
                          {it.legacy_tracking_number && `Legacy: ${it.legacy_tracking_number}`}
                          {(it.tracking_aliases || []).length > 0
                            && ` · Aliassen: ${(it.tracking_aliases || []).join(", ")}`}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      onClick={() => openShipment(it)}
                      className="text-[10px] uppercase font-bold tracking-wide bg-[#0055FF] text-white px-2 py-1 hover:bg-[#0044CC] flex items-center gap-1"
                      data-testid={`shipment-lookup-open-${it.id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> Open zending
                    </button>
                    {canPrintWarehouseReceipt && (
                      <button
                        onClick={() => printWarehouseReceipt(it)}
                        className="text-[10px] uppercase font-bold tracking-wide bg-white text-[#082c50] border border-gray-300 px-2 py-1 hover:bg-gray-50 flex items-center gap-1"
                        data-testid={`shipment-lookup-warehouse-receipt-${it.id}`}
                      >
                        <Printer className="w-3 h-3" /> Warehouse Receipt
                      </button>
                    )}
                    {canPrintShippingLabel && (
                      <button
                        onClick={() => printShippingLabel(it)}
                        className="text-[10px] uppercase font-bold tracking-wide bg-white text-[#082c50] border border-gray-300 px-2 py-1 hover:bg-gray-50 flex items-center gap-1"
                        data-testid={`shipment-lookup-shipping-label-${it.id}`}
                      >
                        <Package className="w-3 h-3" /> Shipping Label
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
